// The real calendar's data (B1–B8 on live data): the same `CalendarSource` the
// demo builds (calendar.demo.ts), read from the operational calendar views,
// the hubs, the therapists' qualifications and the live map's staff snapshot.
// The board cannot tell the two apart — that is the point of the shape.
//
// ⚠ DATA WALL: every read here is staff-side. The views carry a booking id and
// ref, never a client column; the SOS read takes only the therapist id.

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useLiveMapContext } from "@/features/live-map/live-map.hooks";
import { OPERATIONAL_CALENDAR_QUERY_KEY, readCalendarRange } from "./calendar.hooks";
import type { CalendarHub, CalendarSource } from "./calendar-board";
import { calendarHubs, liveStatesFrom } from "./calendar-board.live";
import { weekDaysOf } from "./calendar-board.week";
import { addCalendarDays } from "./calendar-model";

export interface RealCalendar {
  source: CalendarSource | null;
  nowMs: number;
  error: Error | null;
  refetch: () => void;
}

/** Everything the board needs for the week holding `day`; refetches as `day` moves. */
export function useRealCalendarSource(day: string): RealCalendar {
  const weekStart = weekDaysOf(day)[0] ?? day;
  const weekEnd = addCalendarDays(weekStart, 7);

  const week = useQuery({
    // Under the shared prefix, so the calendar's Realtime signal refreshes it.
    queryKey: [...OPERATIONAL_CALENDAR_QUERY_KEY, "board", weekStart],
    queryFn: () => readCalendarRange(weekStart, weekEnd, null, null),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });

  const hubs = useQuery({
    queryKey: ["calendar-hubs"],
    queryFn: async (): Promise<(CalendarHub & { isPilot: boolean })[]> => {
      const { data, error } = await supabase
        .from("hubs")
        .select("id, name, is_pilot")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((hub) => ({
        id: hub.id,
        name: hub.name,
        isPilot: Boolean(hub.is_pilot),
      }));
    },
    staleTime: 5 * 60_000,
  });

  // Who is trained for what — it only steers the Assign list, so a failed read
  // leaves it empty rather than taking the calendar down.
  const skills = useQuery({
    queryKey: ["calendar-skills"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_service_qualifications")
        .select("staff_id, service:services(name)")
        .is("revoked_at", null);
      if (error) throw error;
      const byStaff = new Map<string, string[]>();
      for (const row of data ?? []) {
        const name = row.service?.name;
        if (!name) continue;
        byStaff.set(row.staff_id, [...(byStaff.get(row.staff_id) ?? []), name]);
      }
      return byStaff;
    },
    staleTime: 5 * 60_000,
  });

  const openSos = useQuery({
    queryKey: ["calendar-open-sos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_sos_alerts")
        .select("therapist_id")
        .filter("resolved_at", "is", null);
      if (error) throw error;
      return new Set((data ?? []).map((row) => row.therapist_id));
    },
    refetchInterval: 10_000,
  });

  const live = useLiveMapContext();

  // The board's "now" — the line, the faded past, who is free. A minute is
  // the finest thing it draws.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const source = useMemo((): CalendarSource | null => {
    if (!week.data || !hubs.data) return null;
    const staff = live.data?.clockedInStaff ?? [];
    return {
      models: week.data.models,
      names: week.data.names,
      hubs: calendarHubs(hubs.data, week.data.models),
      clockedIn: new Set(staff.map((member) => member.id)),
      skills: skills.data ?? new Map(),
      live: liveStatesFrom(
        staff.map((member) => ({
          id: member.id,
          mapStatus: member.mapStatus,
          opsStatus: member.opsStatus,
        })),
        openSos.data ?? new Set(),
      ),
    };
  }, [week.data, hubs.data, skills.data, openSos.data, live.data]);

  return {
    source,
    nowMs,
    error: (week.error ?? hubs.error) as Error | null,
    refetch: () => {
      void week.refetch();
      void hubs.refetch();
    },
  };
}
